import { useState, useEffect } from "react";
import { postDataAPI, getDataAPI } from "../../utils/fetchData";
import { useNavigate, useLocation } from "react-router";
import LayoutHeader from "../../layout/LayoutHeader";
import axios from "axios";
import { BASE_URL } from "../../utils/axios";
import usageTracker from "../../utils/usageTracker";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";
import { markReleaseAdded, transitionToPhase, ONBOARDING_PHASES } from "../../utils/onboardingStateMachine";
import CustomCalendar from "../../components/Calendar/CustomCalendar";

const ManualAddCatalog = () => {
    const [editData, setEditData] = useState({
        primary_artists: [''], // Initialize with one empty primary artist field
        featured_artists: [''] // Initialize with one empty featured artist field
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showImportDropdown, setShowImportDropdown] = useState(false);
    const [masterContributors, setMasterContributors] = useState([
        { name: '', role: 'Writer', percentage: '', advanceFee: '' }
    ]);
    const [publishingContributors, setPublishingContributors] = useState([
        { name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' }
    ]);
    const [showHelpText, setShowHelpText] = useState(true);
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const location = useLocation();
    const { modalConfig, showModal, closeModal } = useModal();
    
    // Check if we're in onboarding mode
    const searchParams = new URLSearchParams(location.search);
    const isOnboardingMode = searchParams.get('onboarding') === 'true';

    // Add scroll listener to hide/show help text
    useEffect(() => {
        const handleScroll = (e) => {
            const scrollTop = e.target.scrollTop;
            setShowHelpText(scrollTop < 50); // Hide when scrolled more than 50px
        };

        const scrollContainer = document.querySelector('.manual-add-scroll-container');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Dropdown options - mapped to database values
    const releaseStatusOptions = [
        { label: 'Upcoming', value: 'unreleased' },
        { label: 'Past', value: 'released' },
        { label: 'Demo', value: 'demo' }
    ];
    const genreOptions = [
        'Pop', 'Rock', 'Hip-Hop', 'R&B', 'Country', 'Electronic', 'Jazz', 'Classical', 
        'Folk', 'Blues', 'Reggae', 'Punk', 'Metal', 'Alternative', 'Indie', 'Soul', 
        'Funk', 'Disco', 'House', 'Techno', 'Trap', 'Drill', 'Afrobeats', 'Latin'
    ];
    const keyOptions = [
        'C Major', 'C Minor', 'C# Major', 'C# Minor', 'D Major', 'D Minor', 
        'D# Major', 'D# Minor', 'E Major', 'E Minor', 'F Major', 'F Minor',
        'F# Major', 'F# Minor', 'G Major', 'G Minor', 'G# Major', 'G# Minor',
        'A Major', 'A Minor', 'A# Major', 'A# Minor', 'B Major', 'B Minor'
    ];
    const trackTypeOptions = ['Single', 'Album Track', 'EP Track', 'Remix', 'Live', 'Acoustic'];
    const languageOptions = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Japanese', 'Korean', 'Mandarin', 'Other'];

    const handleImportOption = (option) => {
        setShowImportDropdown(false);
        switch(option) {
            case 'single':
                navigate('/catalog/import');
                break;
            case 'bulk':
                navigate('/catalog/bulk-import');
                break;
            case 'agency':
                navigate('/catalog/login-catalog');
                break;
            default:
                break;
        }
    };

    const handleNumberInput = (key, value) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        setEditData(prev => ({ ...prev, [key]: numericValue }));
    };

    const handleDateChange = (key, value) => {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
            showModal('Invalid Date', 'Please select a future date. For past releases, use single import instead.', 'warning');
            return;
        }
        
        setEditData(prev => ({ ...prev, [key]: value }));
    };

    const addPrimaryArtist = () => {
        if (editData.primary_artists.length < 6) {
            setEditData(prev => ({
                ...prev,
                primary_artists: [...prev.primary_artists, '']
            }));
        }
    };

    const removePrimaryArtist = (index) => {
        if (editData.primary_artists.length > 1) {
            setEditData(prev => ({
                ...prev,
                primary_artists: prev.primary_artists.filter((_, i) => i !== index)
            }));
        }
    };

    const updatePrimaryArtist = (index, value) => {
        setEditData(prev => ({
            ...prev,
            primary_artists: prev.primary_artists.map((artist, i) => i === index ? value : artist)
        }));
    };

    const addFeaturedArtist = () => {
        if (editData.featured_artists.length < 6) {
            setEditData(prev => ({
                ...prev,
                featured_artists: [...prev.featured_artists, '']
            }));
        }
    };

    const removeFeaturedArtist = (index) => {
        if (editData.featured_artists.length > 1) {
            setEditData(prev => ({
                ...prev,
                featured_artists: prev.featured_artists.filter((_, i) => i !== index)
            }));
        }
    };

    const updateFeaturedArtist = (index, value) => {
        setEditData(prev => ({
            ...prev,
            featured_artists: prev.featured_artists.map((artist, i) => i === index ? value : artist)
        }));
    };

    const addMasterContributor = () => {
        setMasterContributors(prev => [...prev, { name: '', role: 'Writer', percentage: '', advanceFee: '' }]);
    };

    const removeMasterContributor = (index) => {
        if (masterContributors.length > 1) {
            setMasterContributors(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updateMasterContributor = (index, key, value) => {
        setMasterContributors(prev => prev.map((contributor, i) => i === index ? { ...contributor, [key]: value } : contributor));
    };

    const addPublishingContributor = () => {
        setPublishingContributors(prev => [...prev, { name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' }]);
    };

    const removePublishingContributor = (index) => {
        if (publishingContributors.length > 1) {
            setPublishingContributors(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updatePublishingContributor = (index, key, value) => {
        setPublishingContributors(prev => prev.map((contributor, i) => i === index ? { ...contributor, [key]: value } : contributor));
    };

    // Calculate percentage totals for validation
    const getMasterPercentageTotal = () => {
        return masterContributors.reduce((total, contributor) => {
            const percentage = parseFloat(contributor.percentage) || 0;
            return total + percentage;
        }, 0);
    };

    const getPublishingPercentageTotal = () => {
        return publishingContributors.reduce((total, contributor) => {
            const percentage = parseFloat(contributor.percentage) || 0;
            return total + percentage;
        }, 0);
    };

    // Check if percentages are valid (equal to 100%)
    const isMasterPercentageValid = () => getMasterPercentageTotal() === 100;
    const isPublishingPercentageValid = () => getPublishingPercentageTotal() === 100;

    const saveData = async () => {
        setIsLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            
            // Validate required fields
            if (!editData.song_title || !editData.song_title.trim()) {
                showModal('Validation Error', 'Song title is required', 'warning');
                setIsLoading(false);
                return;
            }
            
            // Check if at least one primary artist is provided
            const validPrimaryArtists = editData.primary_artists?.filter(artist => artist && artist.trim() !== '') || [];
            if (validPrimaryArtists.length === 0) {
                showModal('Validation Error', 'At least one primary artist is required', 'warning');
                setIsLoading(false);
                return;
            }

            // Prepare data with primary artists, featured artists and writers
            const finalData = {
                ...editData,
                // Ensure strings are properly formatted and not undefined
                song_title: editData.song_title?.trim() || '',
                primary_artist: Array.isArray(editData.primary_artists) 
                    ? editData.primary_artists.filter(artist => artist && artist.trim() !== '').join(', ')
                    : (editData.primary_artists || ''),
                featured_artists: Array.isArray(editData.featured_artists) 
                    ? editData.featured_artists.filter(artist => artist && artist.trim() !== '').join(', ')
                    : (editData.featured_artists || ''),
                // Sanitize other string fields
                album_title: editData.album_title?.trim() || '',
                genre: editData.genre?.trim() || '',
                sub_genre: editData.sub_genre?.trim() || '',
                label: editData.label?.trim() || '',
                isrc: editData.isrc?.trim() || '',
                upc: editData.upc?.trim() || '',
                // Ensure dates are properly formatted or null
                release_date: editData.release_date || null,
                // Ensure numeric fields are properly converted
                track_number: editData.track_number ? parseInt(editData.track_number) : null,
                duration: editData.duration?.trim() || '',
                // Clean up contributors data with proper type conversion
                master_contributors: masterContributors
                    .filter(contributor => contributor.name && contributor.name.trim() !== '')
                    .map(contributor => ({
                        name: contributor.name?.trim() || '',
                        role: contributor.role || '',
                        percentage: parseFloat(contributor.percentage) || 0,
                        advance_fee: parseFloat(contributor.advanceFee) || 0
                    })),
                publishing_contributors: publishingContributors
                    .filter(contributor => contributor.name && contributor.name.trim() !== '')
                    .map(contributor => ({
                        name: contributor.name?.trim() || '',
                        role: contributor.role || '',
                        percentage: parseFloat(contributor.percentage) || 0,
                        pro: contributor.pro || '',
                        ipi_number: contributor.ipiNumber || '',
                        publishing_line: contributor.publishingLine || ''
                    }))
            };

            // Remove any undefined or null values that might cause issues
            Object.keys(finalData).forEach(key => {
                if (finalData[key] === undefined) {
                    delete finalData[key];
                }
            });

            console.log('📚 ManualAdd: Prepared data for save:', finalData);

            // Step 1: Get existing catalog to check for duplicates
            console.log('📚 ManualAdd: Fetching existing catalog to check for duplicates...');
            let existingCatalog = [];
            try {
                const catalogResponse = await getDataAPI('user/getCatalog', token);
                if (catalogResponse?.data?.status === 200 && catalogResponse?.data?.data) {
                    existingCatalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
                    console.log('📚 ManualAdd: Found', existingCatalog?.length || 0, 'existing tracks in catalog');
                }
            } catch (catalogError) {
                console.warn('⚠️ ManualAdd: Could not fetch existing catalog, proceeding without duplicate check:', catalogError);
            }
            
            // Step 2: Check for duplicates
            if (existingCatalog.length > 0) {
                const isDuplicate = existingCatalog.some(existingTrack => {
                    // Normalize strings for comparison (lowercase, trim whitespace)
                    const normalize = (str) => str ? str.toLowerCase().trim() : '';
                    
                    const newSongName = normalize(finalData.song_title);
                    const newArtistName = normalize(finalData.primary_artist);
                    const newISRC = normalize(finalData.isrc);
                    
                    const existingSongName = normalize(existingTrack.song_title || existingTrack.song_name || existingTrack.title);
                    const existingArtistName = normalize(existingTrack.primary_artist || existingTrack.artist_name || existingTrack.artist);
                    const existingISRC = normalize(existingTrack.isrc);
                    
                    // Check for duplicate based on multiple criteria
                    const sameSongAndArtist = newSongName && newArtistName && 
                                            newSongName === existingSongName && 
                                            newArtistName === existingArtistName;
                    
                    const sameISRC = newISRC && existingISRC && newISRC === existingISRC;
                    
                    // Consider it a duplicate if:
                    // 1. Same ISRC (most reliable identifier)
                    // 2. Same song name AND artist name
                    return sameISRC || sameSongAndArtist;
                });
                
                if (isDuplicate) {
                    const songName = finalData.song_title || 'Unknown Track';
                    const artistName = finalData.primary_artist || 'Unknown Artist';
                    
                    showModal('Duplicate Track', `This track "${songName}" by ${artistName} already exists in your catalog and won't be added again.`, 'warning');
                    setIsLoading(false);
                    return;
                }
            }

            // Step 3: Save the track if it's not a duplicate
            console.log('📚 ManualAdd: Sending save request to API...');
            // Backend expects data to be an array, so wrap the single item in an array
            let response = await postDataAPI('user/saveCatalog', {
                data: [finalData]
            }, token);
            
            console.log('📚 ManualAdd: API Response:', response.data);
            
            if (response.data.status === 200) {
                console.log('✅ ManualAdd: Catalog item saved successfully');
                
                // Track catalog usage
                try {
                    await usageTracker.track('catalog_entry_basic', { 
                        source: 'manual_add',
                        song_title: finalData.song_title 
                    });
                } catch (trackingError) {
                    console.error('Usage tracking failed:', trackingError);
                }
                
                // Trigger reminder system update after successful save
                console.log('📅 Manual catalog add - triggering reminder system check...');
                
                // Dispatch storage event to trigger reminder updates
                localStorage.setItem('catalog_updated', Date.now().toString());
                
                // Also dispatch custom event for same-tab updates
                window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                    detail: {
                        key: 'catalog_updated',
                        newValue: Date.now().toString()
                    }
                }));
                
                // Force reminder system to check for updates
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
                }, 500);
                
                // If onboarding mode, mark as complete and navigate to home
                if (isOnboardingMode) {
                    markReleaseAdded();
                    setTimeout(() => {
                        navigate('/');
                    }, 1500);
                } else {
                    setTimeout(() => {
                        navigate('/catalog/view-all');
                    }, 1500);
                }
            } else {
                console.error('❌ ManualAdd: API returned non-200 status:', response.data);
                const errorMessage = response.data.message || 'Error saving catalog item. Please try again.';
                showModal('Save Error', errorMessage, 'error');
            }
        } catch (error) {
            console.error('❌ ManualAdd: Error saving catalog:', error);
            
            // More detailed error handling
            if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const errorData = error.response.data;
                console.error('Server error details:', { status, errorData });
                
                if (status === 500) {
                    showModal('Server Error', 'Server error occurred. Please check your data and try again. If the problem persists, contact support.', 'error');
                } else if (status === 400) {
                    showModal('Invalid Data', 'Invalid data format. Please check all fields and try again.', 'error');
                } else if (status === 401) {
                    showModal('Authentication Error', 'Authentication error. Please log in again.', 'error');
                } else {
                    showModal('Error', `Error ${status}: ${errorData?.message || 'Unknown error occurred'}`, 'error');
                }
            } else if (error.request) {
                // Request was made but no response received
                console.error('Network error:', error.request);
                showModal('Network Error', 'Network error. Please check your connection and try again.', 'error');
            } else {
                // Something else happened
                console.error('Unexpected error:', error.message);
                showModal('Unexpected Error', 'Unexpected error occurred. Please try again.', 'error');
            }
        }
        setIsLoading(false);
    }

    const renderDropdownField = (field, options, value) => {
        return (
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                    {field.label}
                    {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                </label>
                <select
                    value={value}
                    onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-white text-[12px] appearance-none cursor-pointer"
                    style={{ backgroundImage: 'none' }}
                >
                    <option value="">Select {field.label.toLowerCase()}</option>
                    {options.map((option, index) => {
                        // Handle both string and object format for options
                        const optionValue = typeof option === 'object' ? option.value : option;
                        const optionLabel = typeof option === 'object' ? option.label : option;
                        return (
                            <option key={index} value={optionValue}>
                                {optionLabel}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    };

    // Define form fields - separate required and optional
    const requiredFields = [
        { key: 'song_title', label: 'Song Title', placeholder: 'Enter song title', required: true },
        { key: 'status', label: 'Release Status', type: 'dropdown', options: releaseStatusOptions, required: true },
        { key: 'release_date', label: 'Release Date', placeholder: 'Select release date', type: 'date', required: true },
    ];
    
    const optionalFields = [
        { key: 'duration', label: 'Duration (minutes:seconds)', placeholder: 'e.g., 3:45', type: 'duration' },
        { key: 'bpm', label: 'BPM', placeholder: 'Enter BPM', type: 'number' },
        { key: 'key', label: 'Key', type: 'dropdown', options: keyOptions },
        { key: 'genre', label: 'Genre', type: 'dropdown', options: genreOptions },
        { key: 'track_type', label: 'Track Type', type: 'dropdown', options: trackTypeOptions },
        { key: 'language', label: 'Language', type: 'dropdown', options: languageOptions },
        { key: 'explicit', label: 'Explicit Content', type: 'dropdown', options: ['No', 'Yes'] },
        { key: 'isrc', label: 'ISRC', placeholder: 'Enter ISRC code' },
        { key: 'artwork', label: 'Artwork URL', placeholder: 'Enter artwork URL' },
        { key: 'audio_file', label: 'Audio File URL', placeholder: 'Enter audio file URL' },
        { key: 'label', label: 'Record Label', placeholder: 'Enter record label' },
        { key: 'distributor', label: 'Distributor', placeholder: 'Enter distributor' },
        { key: 'spotify_link', label: 'Spotify Link', placeholder: 'Enter Spotify URL' },
        { key: 'apple_music_link', label: 'Apple Music Link', placeholder: 'Enter Apple Music URL' },
        { key: 'youtube_link', label: 'YouTube Link', placeholder: 'Enter YouTube URL' },
        { key: 'sound_cloud_link', label: 'SoundCloud Link', placeholder: 'Enter SoundCloud URL' },
        { key: 'internal_notes', label: 'Internal Notes', placeholder: 'Enter internal notes', type: 'textarea' }
    ];
    
    // Essential fields for onboarding mode (only Title, Release Date, Release Status)
    const onboardingRequiredFields = ['song_title', 'release_date', 'status'];
    
    // Filter fields based on mode
    const currentRequiredFields = isOnboardingMode 
        ? requiredFields.filter(field => onboardingRequiredFields.includes(field.key))
        : requiredFields;
        
    const currentOptionalFields = isOnboardingMode ? [] : optionalFields;

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Manual Add</span>
                        <div className="flex items-center gap-4">
                            {/* Save Button */}
                                <span 
                                    className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={saveData}
                                >
                                Save
                                </span>
                            
                        <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
                        </div>
                    </div>
                    
                    {/* Help Text */}
                    <div className="px-4 mb-6 w-full">
                        <div className="text-[10px] text-[rgba(255,255,255,0.7)] text-left">
                            {isOnboardingMode 
                                ? "Fill in the essential fields below. We'll fill in the rest later!" 
                                : "Fill in the required fields to create your catalog entry."}
                        </div>
                    </div>

                    <div className="manual-add-scroll-container w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">

                            {/* Required Fields First */}
                            <div className="space-y-4 mb-6">
                                {currentRequiredFields.map((field) => {
                                    const value = editData[field.key] || '';
                                    
                                    // Dropdown fields
                                    if (field.type === 'dropdown') {
                                        return (
                                            <div key={field.key}>
                                                {renderDropdownField(field, field.options, value)}
                                            </div>
                                        );
                                    }
                                    
                                    // Date fields with CustomCalendar
                                    if (field.type === 'date') {
                                        return (
                                            <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                <CustomCalendar
                                                    label={field.label + (field.required ? ' *' : '')}
                                                    value={value}
                                                    onChange={(date) => setEditData(prev => ({ ...prev, [field.key]: date }))}
                                                    placeholder={field.placeholder}
                                                    showTime={false}
                                                    required={field.required}
                                                    theme="catalog"
                                                />
                                            </div>
                                        );
                                    }
                                    
                                    // Number fields (BPM)
                                    if (field.type === 'number') {
                                        return (
                                            <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                    {field.label}
                                                    {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) => handleNumberInput(field.key, e.target.value)}
                                                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                    placeholder={field.placeholder}
                                                />
                                            </div>
                                        );
                                    }
                                    
                                    // Duration field with special validation
                                    if (field.type === 'duration') {
                                        return (
                                            <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                    {field.label}
                                                    {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) => {
                                                        // Allow only numbers and colon for duration format
                                                        const durationValue = e.target.value.replace(/[^0-9:]/g, '');
                                                        setEditData(prev => ({ ...prev, [field.key]: durationValue }));
                                                    }}
                                                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                    placeholder={field.placeholder}
                                                />
                                            </div>
                                        );
                                    }
                                    
                                    // Textarea fields
                                    if (field.type === 'textarea') {
                                        return (
                                            <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                    {field.label}
                                                    {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                </label>
                                                <textarea
                                                    value={value}
                                                    onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                                                    placeholder={field.placeholder}
                                                />
                                            </div>
                                        );
                                    }
                                    
                                    // Regular input fields
                                    return (
                                        <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                            <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                {field.label}
                                                {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                            </label>
                                            <input
                                                type={field.type || 'text'}
                                                value={value}
                                                onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder={field.placeholder}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {/* Artist & Contributor Sections */}
                            <div className="space-y-4 mb-6">
                                {/* Primary Artists Section */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-white text-[12px]" style={{ fontWeight: 300 }}>
                                            Primary Artists <span className="text-[#17A2B8]">*</span>
                                        </label>
                                        {editData.primary_artists.length < 6 && (
                                            <button
                                                onClick={addPrimaryArtist}
                                                className="text-[#17A2B8] text-[10px] hover:opacity-70 flex items-center gap-1"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add
                                            </button>
                                        )}
                                    </div>
                                    {editData.primary_artists.map((artist, index) => (
                                        <div key={index} className="flex items-center gap-2 mb-2 last:mb-0">
                                            <input
                                                type="text"
                                                value={artist}
                                                onChange={(e) => updatePrimaryArtist(index, e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder={`Primary artist ${index + 1}`}
                                            />
                                            {editData.primary_artists.length > 1 && (
                                                <button
                                                    onClick={() => removePrimaryArtist(index)}
                                                    className="text-red-400 hover:text-red-300 text-[10px]"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Featured Artists Section */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-white text-[12px]" style={{ fontWeight: 300 }}>Featured Artists</label>
                                        {editData.featured_artists.length < 6 && (
                                            <button
                                                onClick={addFeaturedArtist}
                                                className="text-[#17A2B8] text-[10px] hover:opacity-70 flex items-center gap-1"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add
                                            </button>
                                        )}
                                    </div>
                                    {editData.featured_artists.map((artist, index) => (
                                        <div key={index} className="flex items-center gap-2 mb-2 last:mb-0">
                                            <input
                                                type="text"
                                                value={artist}
                                                onChange={(e) => updateFeaturedArtist(index, e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder={`Featured artist ${index + 1}`}
                                            />
                                            {editData.featured_artists.length > 1 && (
                                                <button
                                                    onClick={() => removeFeaturedArtist(index)}
                                                    className="text-red-400 hover:text-red-300 text-[10px]"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                
                                                                 {/* Master Contributors Section */}
                                 <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                     <div className="flex items-center justify-between mb-2">
                                         <div className="flex items-center gap-2">
                                             <label className="block text-white text-[12px]" style={{ fontWeight: 300 }}>Master Contributors</label>
                                             <span className={`text-[10px] px-2 py-1 rounded ${
                                                 isMasterPercentageValid() 
                                                     ? 'bg-green-500/20 text-green-400' 
                                                     : 'bg-red-500/20 text-red-400'
                                             }`}>
                                                 {getMasterPercentageTotal()}%
                                             </span>
                                         </div>
                                         <button
                                             onClick={addMasterContributor}
                                             className="text-[#17A2B8] text-[10px] hover:opacity-70 flex items-center gap-1"
                                         >
                                             <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                             </svg>
                                             Add
                                         </button>
                                     </div>
                                                                         {masterContributors.map((contributor, index) => (
                                         <div key={index} className="space-y-2 mb-4 last:mb-0 p-3 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.05)]">
                                             <div className="flex items-center gap-2">
                                                 <input
                                                     type="text"
                                                     value={contributor.name}
                                                     onChange={(e) => updateMasterContributor(index, 'name', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                     placeholder={`Master Contributor ${index + 1}`}
                                                 />
                                                 {masterContributors.length > 1 && (
                                                     <button
                                                         onClick={() => removeMasterContributor(index)}
                                                         className="text-red-400 hover:text-red-300 text-[10px] p-1"
                                                     >
                                                         <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                         </svg>
                                                     </button>
                                                 )}
                                             </div>
                                             <div className="flex items-center gap-2">
                                                 <select
                                                     value={contributor.role}
                                                     onChange={(e) => updateMasterContributor(index, 'role', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] appearance-none cursor-pointer"
                                                     style={{ backgroundImage: 'none' }}
                                                 >
                                                     <option value="Writer">Writer</option>
                                                     <option value="Producer">Producer</option>
                                                     <option value="Engineer">Engineer</option>
                                                     <option value="Mixer">Mixer</option>
                                                     <option value="Drummer">Drummer</option>
                                                     <option value="Guitarist">Guitarist</option>
                                                     <option value="Bassist">Bassist</option>
                                                     <option value="Vocalist">Vocalist</option>
                                                     <option value="Other">Other</option>
                                                 </select>
                                                 <input
                                                     type="text"
                                                     value={contributor.percentage}
                                                     onChange={(e) => updateMasterContributor(index, 'percentage', e.target.value)}
                                                     className={`w-16 bg-transparent border rounded px-2 py-1 text-[12px] placeholder-[rgba(255,255,255,0.5)] ${
                                                         isMasterPercentageValid() 
                                                             ? 'border-[rgba(255,255,255,0.1)] text-white' 
                                                             : 'border-red-500 text-red-400'
                                                     }`}
                                                     placeholder="%"
                                                     onKeyPress={(e) => {
                                                         if (!/[0-9.]/.test(e.key)) {
                                                             e.preventDefault();
                                                         }
                                                     }}
                                                 />
                                                 <input
                                                     type="text"
                                                     value={contributor.advanceFee}
                                                     onChange={(e) => updateMasterContributor(index, 'advanceFee', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                     placeholder="Advance/Fee"
                                                 />
                                             </div>
                                         </div>
                                     ))}
                                </div>
                                
                                                                 {/* Publishing Contributors Section */}
                                 <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                     <div className="flex items-center justify-between mb-2">
                                         <div className="flex items-center gap-2">
                                             <label className="block text-white text-[12px]" style={{ fontWeight: 300 }}>Publishing Contributors</label>
                                             <span className={`text-[10px] px-2 py-1 rounded ${
                                                 isPublishingPercentageValid() 
                                                     ? 'bg-green-500/20 text-green-400' 
                                                     : 'bg-red-500/20 text-red-400'
                                             }`}>
                                                 {getPublishingPercentageTotal()}%
                                             </span>
                                         </div>
                                         <button
                                             onClick={addPublishingContributor}
                                             className="text-[#17A2B8] text-[10px] hover:opacity-70 flex items-center gap-1"
                                         >
                                             <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                             </svg>
                                             Add
                                         </button>
                                     </div>
                                                                         {publishingContributors.map((contributor, index) => (
                                         <div key={index} className="space-y-2 mb-4 last:mb-0 p-3 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.05)]">
                                             <div className="flex items-center gap-2">
                                                 <input
                                                     type="text"
                                                     value={contributor.name}
                                                     onChange={(e) => updatePublishingContributor(index, 'name', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                     placeholder={`Publishing Contributor ${index + 1}`}
                                                 />
                                                 {publishingContributors.length > 1 && (
                                                     <button
                                                         onClick={() => removePublishingContributor(index)}
                                                         className="text-red-400 hover:text-red-300 text-[10px] p-1"
                                                     >
                                                         <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                         </svg>
                                                     </button>
                                                 )}
                                             </div>
                                             <div className="flex items-center gap-2">
                                                 <select
                                                     value={contributor.role}
                                                     onChange={(e) => updatePublishingContributor(index, 'role', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] appearance-none cursor-pointer"
                                                     style={{ backgroundImage: 'none' }}
                                                 >
                                                     <option value="Writer">Writer</option>
                                                     <option value="Producer">Producer</option>
                                                     <option value="Engineer">Engineer</option>
                                                     <option value="Mixer">Mixer</option>
                                                     <option value="Drummer">Drummer</option>
                                                     <option value="Guitarist">Guitarist</option>
                                                     <option value="Bassist">Bassist</option>
                                                     <option value="Vocalist">Vocalist</option>
                                                     <option value="Other">Other</option>
                                                 </select>
                                                 <input
                                                     type="text"
                                                     value={contributor.percentage}
                                                     onChange={(e) => updatePublishingContributor(index, 'percentage', e.target.value)}
                                                     className={`w-16 bg-transparent border rounded px-2 py-1 text-[12px] placeholder-[rgba(255,255,255,0.5)] ${
                                                         isPublishingPercentageValid() 
                                                             ? 'border-[rgba(255,255,255,0.1)] text-white' 
                                                             : 'border-red-500 text-red-400'
                                                     }`}
                                                     placeholder="%"
                                                     onKeyPress={(e) => {
                                                         if (!/[0-9.]/.test(e.key)) {
                                                             e.preventDefault();
                                                         }
                                                     }}
                                                 />
                                             </div>
                                             <div className="flex items-center gap-2">
                                                 <select
                                                     value={contributor.pro}
                                                     onChange={(e) => updatePublishingContributor(index, 'pro', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] appearance-none cursor-pointer"
                                                     style={{ backgroundImage: 'none' }}
                                                 >
                                                     <option value="">PRO</option>
                                                     <option value="ASCAP">ASCAP</option>
                                                     <option value="BMI">BMI</option>
                                                     <option value="SESAC">SESAC</option>
                                                     <option value="SOCAN">SOCAN</option>
                                                     <option value="PRS">PRS</option>
                                                     <option value="GEMA">GEMA</option>
                                                     <option value="SACEM">SACEM</option>
                                                     <option value="Other">Other</option>
                                                 </select>
                                                 <input
                                                     type="text"
                                                     value={contributor.ipiNumber}
                                                     onChange={(e) => updatePublishingContributor(index, 'ipiNumber', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                     placeholder="IPI #"
                                                 />
                                             </div>
                                             <div className="flex items-center gap-2">
                                                 <input
                                                     type="text"
                                                     value={contributor.publishingLine}
                                                     onChange={(e) => updatePublishingContributor(index, 'publishingLine', e.target.value)}
                                                     className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                     placeholder="Publishing Line"
                                                 />
                                             </div>
                                         </div>
                                     ))}
                                </div>
                            </div>
                            
                            {/* Optional Fields */}
                            {!isOnboardingMode && (
                                <div className="space-y-4 mb-6">
                                    {currentOptionalFields.map((field) => {
                                        const value = editData[field.key] || '';
                                        
                                        // Dropdown fields
                                        if (field.type === 'dropdown') {
                                            return (
                                                <div key={field.key}>
                                                    {renderDropdownField(field, field.options, value)}
                                                </div>
                                            );
                                        }
                                        
                                        // Date fields with CustomCalendar
                                        if (field.type === 'date') {
                                            return (
                                                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                    <CustomCalendar
                                                        label={field.label + (field.required ? ' *' : '')}
                                                        value={value}
                                                        onChange={(date) => setEditData(prev => ({ ...prev, [field.key]: date }))}
                                                        placeholder={field.placeholder}
                                                        showTime={false}
                                                        required={field.required}
                                                        theme="catalog"
                                                    />
                                                </div>
                                            );
                                        }
                                        
                                        // Number fields (BPM)
                                        if (field.type === 'number') {
                                            return (
                                                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                        {field.label}
                                                        {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={value}
                                                        onChange={(e) => handleNumberInput(field.key, e.target.value)}
                                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                        placeholder={field.placeholder}
                                                    />
                                                </div>
                                            );
                                        }
                                        
                                        // Duration field with special validation
                                        if (field.type === 'duration') {
                                            return (
                                                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                        {field.label}
                                                        {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={value}
                                                        onChange={(e) => {
                                                            // Allow only numbers and colon for duration format
                                                            const durationValue = e.target.value.replace(/[^0-9:]/g, '');
                                                            setEditData(prev => ({ ...prev, [field.key]: durationValue }));
                                                        }}
                                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                        placeholder={field.placeholder}
                                                    />
                                                </div>
                                            );
                                        }
                                        
                                        // Textarea fields
                                        if (field.type === 'textarea') {
                                            return (
                                                <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                    <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                        {field.label}
                                                        {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                    </label>
                                                    <textarea
                                                        value={value}
                                                        onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                                                        placeholder={field.placeholder}
                                                    />
                                                </div>
                                            );
                                        }
                                        
                                        // Regular input fields
                                        return (
                                            <div key={field.key} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                                                    {field.label}
                                                    {field.required && <span className="text-[#17A2B8] ml-1">*</span>}
                                                </label>
                                                <input
                                                    type={field.type || 'text'}
                                                    value={value}
                                                    onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                    placeholder={field.placeholder}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                <button
                                    onClick={saveData}
                                    disabled={isLoading}
                                    className="w-full bg-[#17A2B8] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(23,162,184,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Catalog Item'
                                    )}
                                </button>
                    </div>
                        </div>

                        {/* Help Text - Hidden on scroll */}
                        {showHelpText && (
                            <div className="mt-6 mb-4">
                                <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                    <p>Fill in as much information as you have available for your track.</p>
                                    <p>You can always edit these details later from the catalog list.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                </div>
                
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default ManualAddCatalog;